import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { Form } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';

import { FIELD_KEYS } from '_constants/forms';
import { ICON_ALIASES } from '_constants/icons';
import { theme } from '_constants/theme';
import { getFieldProps } from '_helpers/forms/fields';
import { usePrevious } from '_hooks';

import Button from 'components/atomic/Button';
import Icon from 'components/atomic/Icon';
import SelectField from 'components/atomic/fields/SelectField';
import Text from 'components/atomic/layouts/Text';

const StyledForm = styled(Form)`
  position: relative;
  z-index: 1;
`;

const StyledIconContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 48px;
  width: 48px;
  margin-bottom: ${theme.spaces.large6X}px;
  border-radius: 50%;
  background: ${theme.colors.indigo.primary};
`;

const StyledFieldsContainer = styled.div`
  margin-bottom: ${theme.spaces.large6X}px;
  padding: ${theme.spaces.regular}px;
  border-radius: 16px;
  background: ${theme.colors.white};
`;

const AmendmentRequestForm = ({ taxReturnOptions = [], error, loading, submitDisabled, defaultValues, onSubmit }) => {
  const {
    control,
    formState: { isSubmitted, isSubmitSuccessful, submitCount },
    reset,
    handleSubmit,
  } = useForm({
    defaultValues,
    reValidateMode: 'onChange',
  });

  const prevLoading = usePrevious(loading);

  useEffect(() => {
    if (isSubmitted && !isSubmitSuccessful && submitCount) {
      onSubmit();
    }
  }, [isSubmitted, isSubmitSuccessful, submitCount, onSubmit]);

  useEffect(() => {
    if (prevLoading && !loading && !error) {
      reset({ ...defaultValues });
    }
  }, [prevLoading, loading, error, reset]);

  return (
    <StyledForm noValidate onSubmit={handleSubmit(onSubmit)}>
      <StyledIconContainer>
        <Icon alias={ICON_ALIASES.receiptDiscount} color={theme.colors.indigo.primary} />
      </StyledIconContainer>

      <Text size={theme.fontSizes.largeX} weight={600} bottomSpace={theme.spaces.large6X}>
        Need to <br />
        File an Amendment?
      </Text>

      <StyledFieldsContainer>
        <SelectField
          {...getFieldProps(FIELD_KEYS.returnToAmend)}
          options={taxReturnOptions}
          control={control}
          bottomSpace={theme.spaces.regular}
        />
        <SelectField {...getFieldProps(FIELD_KEYS.reasonForAmendment)} control={control} />
      </StyledFieldsContainer>

      <Text
        size={theme.fontSizes.small - 1}
        weight={500}
        color={theme.colors.gray.secondary}
        bottomSpace={theme.spaces.large6X}
      >
        A tax expert will reach out to you within 24 hrs <br />
        to discuss amending this return
      </Text>

      <Button type="submit" disabled={submitDisabled} color={theme.colors.indigo.primary}>
        Submit Request to Amend
      </Button>

      {error && (
        <Text size={theme.fontSizes.small} color={theme.colors.red.primary} topSpace={theme.spaces.regular}>
          {error?.message}
        </Text>
      )}
    </StyledForm>
  );
};

AmendmentRequestForm.propTypes = {
  taxReturnOptions: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      value: PropTypes.string,
    })
  ),
  loading: PropTypes.bool,
  error: PropTypes.object,
  submitDisabled: PropTypes.bool,
  defaultValues: PropTypes.object,
  onSubmit: PropTypes.func,
};

export default AmendmentRequestForm;
